import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import BookingPanel from "@/components/site/BookingPanel";
import Stars from "@/components/site/Stars";
import { YachtReviews } from "@/components/site/Reviews";
import ShareButtons from "@/components/site/ShareButtons";
import { useLang } from "@/lib/i18n";
import { api, fmtUSD, imgSrc } from "@/lib/api";
import type { Yacht } from "@/types";

function Gallery({ photos, name }: { photos: string[]; name: string }) {
  const [active, setActive] = useState(0);
  const list = photos.length ? photos : [""];
  return (
    <div>
      <div className="aspect-[16/10] overflow-hidden rounded-xl bg-slate-200">
        <img key={active} src={imgSrc(list[active])} alt={name} data-testid="gallery-main-image" className="h-full w-full object-cover rise" />
      </div>
      {list.length > 1 && (
        <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
          {list.map((p, i) => (
            <button key={p + i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
              className={`h-20 w-28 shrink-0 overflow-hidden rounded-md border-2 transition-colors ${i === active ? "border-[#D4AF37]" : "border-transparent opacity-70 hover:opacity-100"}`}>
              <img src={imgSrc(p)} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function YachtDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { t, pick } = useLang();
  const { data: yacht, isLoading, isError } = useQuery<Yacht>({ queryKey: ["yacht", id], queryFn: async () => (await api.get(`/yachts/${id}`)).data });
  const { data: blocked = [], refetch } = useQuery<string[]>({ queryKey: ["blocked", id], queryFn: async () => (await api.get(`/yachts/${id}/blocked`)).data });

  if (isLoading) return <div className="py-40 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-[#00A8A0]" /></div>;
  if (isError || !yacht) return <div className="py-40 text-center"><Link to="/yachts" className="underline">{t("detail.back")}</Link></div>;

  const specs = [
    [t("detail.length"), `${yacht.length_ft} ${t("card.ft")}`],
    [t("detail.capacity"), `${yacht.capacity} ${t("card.guests")}`],
    [t("detail.cabins"), yacht.cabins],
    [t("detail.port"), yacht.port],
  ];
  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="yacht-detail-page">
      <Link to="/yachts" data-testid="detail-back-link" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0B192C] transition-colors"><ArrowLeft className="h-4 w-4" />{t("detail.back")}</Link>
      <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{t(`type.${yacht.type}`)}</p>
          <h1 data-testid="yacht-detail-name" className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl text-[#0B192C]">{yacht.name}</h1>
          {yacht.rating && (
            <div className="mt-3 flex items-center gap-2" data-testid="yacht-detail-rating">
              <Stars value={yacht.rating.avg} testId="yacht-detail-stars" />
              <span className="text-sm text-slate-600">{yacht.rating.avg.toFixed(1)} · {yacht.rating.count === 1 ? t("rev.one") : t("rev.count", { n: yacht.rating.count })}</span>
            </div>
          )}
          <div className="mt-5">
            <ShareButtons title={yacht.name} text={t("share.text", { name: yacht.name, price: fmtUSD(Math.min(...Object.values(yacht.prices).filter((p): p is number => typeof p === "number" && p > 0))) })} />
          </div>
        </div>
      </div>
      <div className="mt-10 grid gap-10 lg:grid-cols-[1.6fr_1fr] items-start">
        <div className="min-w-0 space-y-14">
          <Gallery photos={yacht.photos} name={yacht.name} />
          <section>
            <h2 className="font-display text-2xl sm:text-3xl text-[#0B192C]">{t("detail.specs")}</h2>
            <dl className="mt-6 grid grid-cols-2 sm:grid-cols-4 border-t border-slate-200">
              {specs.map(([k, v]) => (
                <div key={String(k)} className="py-5 pr-4 border-b border-slate-200">
                  <dt className="text-xs uppercase tracking-[0.2em] text-slate-500">{k}</dt>
                  <dd className="mt-2 font-display text-xl text-[#0B192C]">{v}</dd>
                </div>
              ))}
            </dl>
          </section>
          <section>
            <h2 className="font-display text-2xl sm:text-3xl text-[#0B192C]">{t("detail.about")}</h2>
            <p data-testid="yacht-detail-description" className="mt-4 text-base leading-relaxed text-slate-600 max-w-2xl">{pick(yacht, "description")}</p>
          </section>
          {yacht.amenities.length > 0 && (
            <section>
              <h2 className="font-display text-2xl sm:text-3xl text-[#0B192C]">{t("detail.amenities")}</h2>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {yacht.amenities.map((a) => (
                  <li key={a} className="flex items-center gap-3 text-slate-700"><span className="grid h-6 w-6 place-items-center rounded-full bg-[#00D2C8]/15"><Check className="h-3.5 w-3.5 text-[#008C86]" /></span>{t(`am.${a}`)}</li>
                ))}
              </ul>
            </section>
          )}
          <YachtReviews yachtId={yacht.id} />
        </div>
        <aside className="lg:sticky lg:top-28">
          <BookingPanel yacht={yacht} blocked={blocked} initDate={params.get("date") || ""} initGuests={Number(params.get("guests")) || 2} refresh={() => refetch()} />
        </aside>
      </div>
    </div>
  );
}
